import type { PostFormFile } from '@/models/form';
import BlobService from '@/services/blobService';
import FormService from '@/services/formService';
import { isImage, throwIfAborted, pickedBlob, type Uploader } from './fileUploads';

/**
 * An entry's uploader: presign against the form, post the bytes to S3, then `PATCH blob/:id`, whose
 * answer is what makes the file stored. It uploads the file as picked, a HEIC included, and never
 * converts it (docs/memory/entry-files.md).
 */
export const createS3Uploader = (formIdentifier: string): Uploader => ({
  // The slot goes unused until spdata-api's A6 binds presign to a field (ENG-9088 decision 7). The
  // switch from `withoutItemId` to the slot happens here, and neither caller changes.
  async upload(_slot, file, onProgress, signal) {
    const fileData: PostFormFile = {
      filename: file.name,
      filetype: file.type,
      size: file.size,
      withoutItemId: true
    };
    const presignedData = await FormService.postFormFile(formIdentifier, fileData);
    throwIfAborted(signal);
    await FormService.uploadFileToS3WithProgress(pickedBlob(file), presignedData.presignedPost, { onProgress, signal });
    throwIfAborted(signal);
    const stored = await BlobService.patchBlob(presignedData.blob.identifier);
    throwIfAborted(signal);

    // PrimeVue made an object URL for a picked image. The tile draws it until the server's
    // thumbnail lands, and `hasThumbnail` stays whatever the API says.
    const objectUrl = (file as File & { objectURL?: string }).objectURL;
    if (isImage(file)) {
      stored.file = file;
      stored.localPreviewUrl = objectUrl;
    }
    return stored;
  },

  readMetadata: identifier => BlobService.getBlobMetadata(identifier),

  download: (identifier, fileName) => BlobService.downloadBlob(identifier, fileName)
});
